import numeral from 'numeral';
import 'numeral/locales';

/**
 * Format currency values based on the selected currency and locale
 * @param {number} value - The base value (usually in NOK)
 * @param {object} selectedCurrency - Currency object with value, currency, and locale
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, selectedCurrency) => {
  if (!value || !selectedCurrency?.value) return '0';
  
  const convertedValue = Math.round(value * selectedCurrency.value);
  let locale = selectedCurrency.locale || 'no';
  
  // Set numeral locale
  numeral.locale(locale);
  
  // For smaller currencies like EUR, USD, GBP - show more precise values
  if (['EUR', 'USD', 'GBP'].includes(selectedCurrency.currency)) {
    if (convertedValue >= 1000) {
      return numeral(convertedValue).format('0,0');
    } else {
      return numeral(convertedValue).format('0a');
    }
  }
  
  // For larger currencies like NOK, SEK - use abbreviated format
  // Use '0a' for clean abbreviations without decimals in the abbreviated form
  return numeral(convertedValue).format('0a');
};

/**
 * Format non-monetary numbers based on locale (reach, engagement, etc.)
 * @param {number} value - The number to format
 * @param {object} selectedCurrency - Currency object with locale information
 * @param {string} format - Optional format string (defaults to '0.[0]a')
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, selectedCurrency, format = '0a') => {
  if (!value && value !== 0) return '0';
  
  const locale = selectedCurrency?.locale || 'no';
  
  // Set numeral locale
  numeral.locale(locale);

  
  return numeral(value).format(format);
};

/**
 * Format percentage values based on locale
 * @param {number} value - The percentage value (0-100)
 * @param {object} selectedCurrency - Currency object with locale information
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, selectedCurrency) => {
  if (!value && value !== 0) return '0%';
  
  const locale = selectedCurrency?.locale || 'no';
  
  // Set numeral locale
  numeral.locale(locale);
  
  return numeral(value).format('0.[0]%');
};

/**
 * Format date to short readable format (e.g., "Jan 5" or "5. jan")
 * @param {string} timestamp - The timestamp to format
 * @param {object} selectedCurrency - Currency object with locale information
 * @returns {string} Formatted date string
 */
export const formatShortDate = (timestamp, selectedCurrency) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const locale = selectedCurrency?.locale === 'no' ? 'nb-NO' : 'en-US';
  
  // Format: "5. jan" for Norwegian, "Jan 5" for English
  const options = { month: 'short', day: 'numeric' };
  return date.toLocaleDateString(locale, options);
};